import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Container, Typography, Paper, Grid, Card, CardContent,
  Button, TextField, MenuItem, Alert, CircularProgress,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  IconButton, Chip, Dialog, DialogTitle, DialogContent, DialogActions
} from '@mui/material';
import {
  Today as UpdateIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Send as SendIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { teacherAPI } from '../../services/api';
import ResponsiveDataView from '@components/mobile/ResponsiveDataView';

const MOODS = ['Happy', 'Calm', 'Energetic', 'Tired', 'Upset', 'Sick'];
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '—';

const today = () => new Date().toISOString().slice(0, 10);

const emptyForm = { student_id: '', update_date: today(), content: '', meals: '', nap: '', mood: '', activities: '' };

export default function PostDailyUpdate() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [students, setStudents] = useState([]);
  const [myUpdates, setMyUpdates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [form, setForm] = useState(emptyForm);
  const [editId, setEditId] = useState(null);
  const [deleteDialog, setDeleteDialog] = useState(null);

  const loadStudents = useCallback(async () => {
    try {
      const res = await teacherAPI.dailyUpdates.eligibleStudents();
      setStudents(res.data.students || []);
    } catch { /* non-fatal */ }
  }, []);

  const loadUpdates = useCallback(async () => {
    try {
      const res = await teacherAPI.dailyUpdates.my();
      setMyUpdates(res.data.updates || []);
    } catch { /* non-fatal */ }
  }, []);

  useEffect(() => {
    Promise.all([loadStudents(), loadUpdates()]).finally(() => setLoading(false));
  }, [loadStudents, loadUpdates]);

  const handleSubmit = async () => {
    if (!form.student_id || !form.content.trim()) {
      setError('Student and content are required');
      return;
    }
    setSaving(true); setError(''); setSuccess('');
    try {
      if (editId) {
        await teacherAPI.dailyUpdates.edit(editId, form);
        setSuccess('Update saved');
        setEditId(null);
      } else {
        await teacherAPI.dailyUpdates.post(form);
        setSuccess('Update posted successfully');
      }
      setForm(emptyForm);
      await loadUpdates();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to post update');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (u) => {
    setEditId(u.id);
    setForm({
      student_id:  u.student_id,
      update_date: u.update_date?.slice(0, 10) || today(),
      content:     u.content || '',
      meals:       u.meals || '',
      nap:         u.nap || '',
      mood:        u.mood || '',
      activities:  u.activities || '',
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDelete = async () => {
    try {
      await teacherAPI.dailyUpdates.remove(deleteDialog.id);
      setDeleteDialog(null);
      setSuccess('Update deleted');
      await loadUpdates();
    } catch {
      setError('Failed to delete update');
    }
  };

  return (
    <>
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box display="flex" alignItems="center" gap={2} mb={3}>
          <UpdateIcon color="primary" sx={{ fontSize: 32 }} />
          <Box>
            <Typography variant="h5" fontWeight={700}>Daily Updates</Typography>
            <Typography variant="body2" color="text.secondary">Post per-child updates for parents</Typography>
          </Box>
        </Box>

        {error   && <Alert severity="error"   sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

        {/* Post form */}
        <Paper elevation={2} sx={{ p: 3, mb: 4 }}>
          <Typography variant="h6" fontWeight={600} mb={2}>
            {editId ? 'Edit Update' : 'Post New Update'}
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                select fullWidth label="Student" value={form.student_id}
                onChange={e => setForm(f => ({ ...f, student_id: e.target.value }))}
                disabled={!!editId}
              >
                {students.map(s => (
                  <MenuItem key={s.id} value={s.id}>{s.name} ({s.admission_number})</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth type="date" label="Date" value={form.update_date}
                onChange={e => setForm(f => ({ ...f, update_date: e.target.value }))}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth multiline minRows={3} label="Update / Notes *"
                placeholder="Describe the child's day, activities, observations…"
                value={form.content}
                onChange={e => setForm(f => ({ ...f, content: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth label="Meals" placeholder="e.g. Ate lunch well, had snack"
                value={form.meals} onChange={e => setForm(f => ({ ...f, meals: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth label="Nap" placeholder="e.g. 1 hour nap at 1 PM"
                value={form.nap} onChange={e => setForm(f => ({ ...f, nap: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                select fullWidth label="Mood" value={form.mood}
                onChange={e => setForm(f => ({ ...f, mood: e.target.value }))}
              >
                <MenuItem value="">— Not specified —</MenuItem>
                {MOODS.map(m => <MenuItem key={m} value={m}>{m}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth label="Activities" placeholder="e.g. Art, outdoor play, storytime"
                value={form.activities} onChange={e => setForm(f => ({ ...f, activities: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12}>
              <Box display="flex" gap={1}>
                <Button
                  variant="contained" startIcon={<SendIcon />}
                  onClick={handleSubmit} disabled={saving}
                >
                  {saving ? 'Posting…' : editId ? 'Save Changes' : 'Post Update'}
                </Button>
                {editId && (
                  <Button variant="outlined" onClick={() => { setEditId(null); setForm(emptyForm); }}>
                    Cancel
                  </Button>
                )}
              </Box>
            </Grid>
          </Grid>
        </Paper>

        {/* Recent updates */}
        <Typography variant="h6" fontWeight={600} mb={2}>Posted Updates</Typography>
        {loading ? (
          <Box display="flex" justifyContent="center" mt={4}><CircularProgress /></Box>
        ) : myUpdates.length === 0 ? (
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <Typography color="text.secondary">No updates posted yet.</Typography>
          </Paper>
        ) : (
          <ResponsiveDataView
            rows={myUpdates}
            keyExtractor={(u) => u.id}
            renderCard={(u) => (
              <Card variant="outlined" sx={{ mb: 1.5 }}>
                <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
                  <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={0.5}>
                    <Box>
                      <Typography variant="body2" fontWeight={600}>{u.student_name}</Typography>
                      <Typography variant="caption" color="text.secondary">{u.admission_number}</Typography>
                    </Box>
                    {u.mood && <Chip label={u.mood} size="small" />}
                  </Box>
                  <Typography variant="body2" sx={{ mb: 1 }}>{u.content}</Typography>
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="caption" color="text.secondary">{fmtDate(u.update_date)}</Typography>
                    <Box>
                      <IconButton size="small" onClick={() => handleEdit(u)} title="Edit">
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" color="error" onClick={() => setDeleteDialog(u)} title="Delete">
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            )}
            table={
              <TableContainer component={Paper} elevation={1} sx={{ overflowX: 'auto' }}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>Student</strong></TableCell>
                      <TableCell><strong>Date</strong></TableCell>
                      <TableCell><strong>Content</strong></TableCell>
                      <TableCell><strong>Mood</strong></TableCell>
                      <TableCell align="center"><strong>Actions</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {myUpdates.map(u => (
                      <TableRow key={u.id} hover>
                        <TableCell>
                          <Typography variant="body2" fontWeight={600}>{u.student_name}</Typography>
                          <Typography variant="caption" color="text.secondary">{u.admission_number}</Typography>
                        </TableCell>
                        <TableCell>{fmtDate(u.update_date)}</TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {u.content}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {u.mood ? <Chip label={u.mood} size="small" /> : '—'}
                        </TableCell>
                        <TableCell align="center">
                          <IconButton size="small" onClick={() => handleEdit(u)} title="Edit">
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton size="small" color="error" onClick={() => setDeleteDialog(u)} title="Delete">
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            }
          />
        )}
      </Container>

      <Dialog open={!!deleteDialog} onClose={() => setDeleteDialog(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete Update</DialogTitle>
        <DialogContent>
          <Typography>Delete the update for <strong>{deleteDialog?.student_name}</strong> on {fmtDate(deleteDialog?.update_date)}?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog(null)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleDelete}>Delete</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
